import { useEffect, useCallback } from 'react';

interface UseModalOptions {
  isOpen: boolean;
  onClose: () => void;
  preventBodyScroll?: boolean;
  closeOnEscape?: boolean;
  closeOnClickOutside?: boolean;
}

export const useModal = ({
  isOpen,
  onClose,
  preventBodyScroll = true,
  closeOnEscape = true,
  closeOnClickOutside = true
}: UseModalOptions) => {
  
  // Handle body scroll prevention (mobile-optimized)
  useEffect(() => {
    if (!isOpen || !preventBodyScroll) return;
    
    // Store current scroll position
    const scrollY = window.scrollY;
    const originalStyle = window.getComputedStyle(document.body).overflow;
    
    // Apply mobile-friendly scroll lock
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.classList.add('modal-open');
    
    return () => {
      // Restore original state and scroll position
      document.body.style.overflow = originalStyle;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.classList.remove('modal-open');
      window.scrollTo(0, scrollY);
    };
  }, [isOpen, preventBodyScroll]);

  // Handle ESC key
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;
    
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape, onClose]);

  // Focus management
  useEffect(() => {
    if (!isOpen) return;
    
    // Store the currently focused element
    const previouslyFocusedElement = document.activeElement as HTMLElement;
    
    // Focus the modal container after a small delay
    const timeoutId = setTimeout(() => {
      const modalElement = document.querySelector('[role="dialog"]') as HTMLElement;
      if (modalElement) {
        modalElement.focus();
      }
    }, 100);
    
    return () => {
      clearTimeout(timeoutId);
      // Restore focus to the previously focused element
      if (previouslyFocusedElement && previouslyFocusedElement.focus) {
        previouslyFocusedElement.focus();
      }
    };
  }, [isOpen]);

  // Focus trap
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isOpen || e.key !== 'Tab') return;
    
    const modalElement = e.currentTarget;
    const focusableElements = modalElement.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
    
    if (e.shiftKey) {
      if (document.activeElement === firstElement) {
        lastElement?.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === lastElement) {
        firstElement?.focus();
        e.preventDefault();
      }
    }
  }, [isOpen]);

  // Click outside handler
  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (closeOnClickOutside && e.target === e.currentTarget) {
      onClose();
    }
  }, [closeOnClickOutside, onClose]);

  return {
    handleKeyDown,
    handleBackdropClick,
  };
};

export default useModal;